import { authClient, signIn } from "@/lib/auth-client";
import { safeNextPath } from "@/lib/safe-redirect";
import { Sentry } from "@/lib/sentry";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

/**
 * Resend the email-verification link via Better-Auth. Errors are reported to
 * Sentry but never surfaced to the caller — the UI shows a generic
 * "sent again" message regardless to avoid revealing whether the address
 * exists in the system. (AP-002 remediation, R2c.)
 *
 * Exported for direct unit-test coverage of the Sentry-capture path.
 */
export async function resendVerificationEmail(email: string): Promise<void> {
	try {
		await authClient.sendVerificationEmail({ email });
	} catch (err) {
		// Tag with feature/step only — never include the email itself
		// per `.claude/rules/security.md` "Never log" (PII).
		Sentry.captureException(err, {
			tags: { area: "login", step: "resend-verification" },
		});
		// Swallow intentionally: caller always shows the generic
		// "sent again" UX so we don't reveal account existence.
	}
}

export const Route = createFileRoute("/login")({
	component: LoginPage,
	// `next` carries the page the user was trying to reach when the session
	// check bounced them here — typically a deep link from a notification
	// email. Untrusted: always run it through safeNextPath before navigating.
	validateSearch: z.object({ next: z.string().optional() }),
});

type LoginState =
	| { kind: "idle" }
	| { kind: "error"; message: string }
	| { kind: "unverified"; email: string; resent: boolean };

function LoginPage() {
	const search = Route.useSearch();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [state, setState] = useState<LoginState>({ kind: "idle" });
	const [loading, setLoading] = useState(false);
	const [resending, setResending] = useState(false);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setState({ kind: "idle" });
		setLoading(true);

		try {
			const result = await signIn.email({ email, password });
			if (result.error) {
				// Better-Auth surfaces "Email not verified" for the unverified
				// path. Route to the resend-verification UX instead of a plain
				// error.
				const code = result.error.code;
				const message = result.error.message ?? "Login failed";
				if (code === "EMAIL_NOT_VERIFIED" || /not.*verified/i.test(message)) {
					setState({ kind: "unverified", email, resent: false });
				} else {
					setState({ kind: "error", message });
				}
				return;
			}
			// When 2FA is required, the twoFactorClient plugin's
			// onTwoFactorRedirect hook navigates to /two-factor before we get
			// here. Skip the home redirect in that case so we don't stomp on
			// the challenge page.
			const twoFactorRedirect = (result.data as { twoFactorRedirect?: boolean } | undefined)
				?.twoFactorRedirect;
			if (twoFactorRedirect) return;
			window.location.href = safeNextPath(search.next);
		} catch {
			setState({ kind: "error", message: "An unexpected error occurred" });
		} finally {
			setLoading(false);
		}
	}

	async function handleResendVerification() {
		if (state.kind !== "unverified") return;
		setResending(true);
		// `resendVerificationEmail` reports errors to Sentry internally and
		// never throws — the UI always shows the generic "sent again" UX so
		// we don't reveal whether the address exists in the system.
		await resendVerificationEmail(state.email);
		setState({ ...state, resent: true });
		setResending(false);
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-[#f8f9fb]">
			<div className="w-full max-w-sm space-y-6 rounded-lg border border-[#e5e7eb] bg-white p-8 shadow-sm">
				<div className="text-center">
					<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#16a34a]">
						<span className="text-lg font-bold text-white">P</span>
					</div>
					<h1 className="text-xl font-semibold text-[#1e1e20]">PayrollMart</h1>
					<p className="mt-1 text-sm text-[#6b7280]">Sign in to your account</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-4">
					{state.kind === "error" && (
						<div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{state.message}</div>
					)}

					{state.kind === "unverified" && (
						<div className="space-y-2 rounded bg-[#fffbeb] p-3 text-sm text-[#92400e]">
							<p>
								Your email isn't verified yet. Check your inbox for the link we sent to{" "}
								<strong>{state.email}</strong>.
							</p>
							{state.resent ? (
								<p className="text-xs text-[#78350f]">Sent again. Give it a minute to arrive.</p>
							) : (
								<button
									type="button"
									onClick={handleResendVerification}
									disabled={resending}
									className="text-xs font-medium text-[#92400e] underline hover:text-[#78350f] disabled:opacity-50"
								>
									{resending ? "Sending…" : "Resend verification email"}
								</button>
							)}
						</div>
					)}

					<div className="space-y-1">
						<label htmlFor="email" className="text-xs font-medium text-[#1e1e20]">
							Email
						</label>
						<input
							id="email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							autoComplete="email"
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
							placeholder="you@example.com"
						/>
					</div>

					<div className="space-y-1">
						<label htmlFor="password" className="text-xs font-medium text-[#1e1e20]">
							Password
						</label>
						<input
							id="password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							required
							autoComplete="current-password"
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						/>
						<div className="pt-0.5 text-right">
							<Link to="/forgot-password" className="text-xs text-[#4f46e5] hover:text-[#3730a3]">
								Forgot password?
							</Link>
						</div>
					</div>

					<button
						type="submit"
						disabled={loading}
						className="h-9 w-full rounded bg-[#16a34a] text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#15803d] disabled:opacity-50"
					>
						{loading ? "Signing in..." : "Sign in"}
					</button>
				</form>
			</div>
		</div>
	);
}
